import axios from 'axios'
import { platformEnv } from '@/platform/config/env'
import { attachAuthInterceptors } from '@/platform/http/authBridge'
import { createHttpClient } from '@/platform/http/client'
import { nextAssetVersionUrl } from '@/platform/media/assetVersion'
import type {
  Course,
  CourseDetailResponse,
  CourseDraft,
  CourseListResponse,
  CourseTaxonomyTerm,
  Lesson,
  LessonDraft,
} from '../model/course'

const client = createHttpClient(platformEnv.courseApiUrl)
const mediaClient = axios.create({ baseURL: platformEnv.gatewayApiUrl, timeout: 60_000 })
attachAuthInterceptors(mediaClient)

export type ImageUploadStage = 'converting' | 'presigning' | 'uploading'

export async function convertImageToAvif(file: Blob): Promise<Blob> {
  if (file.type === 'image/avif') return file
  const formData = new FormData()
  formData.append('file', file, 'course-cover')
  formData.append('format', 'avif')
  const response = await mediaClient.post<Blob>('/v1/admin/media/image/convert', formData, { responseType: 'blob' })
  return new Blob([response.data], { type: 'image/avif' })
}

export interface CourseFilterParams {
  page?: number
  page_size?: number
  search?: string
  subject_id?: number | null
  term_id?: number | null
}

export const coursesApi = {
  async listCourses(params?: CourseFilterParams): Promise<CourseListResponse> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.page_size) searchParams.set('page_size', String(params.page_size))
    if (params?.search?.trim()) searchParams.set('search', params.search.trim())
    if (params?.subject_id) searchParams.set('subject_id', String(params.subject_id))
    if (params?.term_id) searchParams.set('term_id', String(params.term_id))

    const query = searchParams.toString()
    const url = `/v1/admin/courses${query ? `?${query}` : ''}`
    const res = await client.request<Course[]>({ url, method: 'GET' })
    const items = Array.isArray(res.data) ? res.data : []
    const total = res.pagination?.total ?? items.length
    const page = res.pagination?.page ?? params?.page ?? 1
    const pageSize = res.pagination?.size ?? params?.page_size ?? 20
    const totalPages = Math.ceil(total / pageSize) || 1

    return {
      items,
      pagination: {
        page,
        page_size: pageSize,
        total,
        total_pages: totalPages,
      },
    }
  },

  async getCourse(id: number): Promise<CourseDetailResponse> {
    const res = await client.request<Record<string, unknown>>({ url: `/v1/admin/courses/${id}`, method: 'GET' })
    const raw = res.data || {}
    const terms = (raw.taxonomy_terms || raw.terms || []) as CourseTaxonomyTerm[]
    const course = (raw.course ? raw.course : raw) as Course
    return {
      course,
      terms,
    }
  },

  async createCourse(payload: CourseDraft): Promise<Course> {
    return (await client.request<Course>({ url: '/v1/admin/courses', method: 'POST', data: payload })).data
  },

  async updateCourse(id: number, payload: Partial<CourseDraft>): Promise<Course> {
    return (await client.request<Course>({ url: `/v1/admin/courses/${id}`, method: 'PUT', data: payload })).data
  },

  async deleteCourse(id: number): Promise<void> {
    await client.request<void>({ url: `/v1/admin/courses/${id}`, method: 'DELETE' })
  },

  /**
   * Upload ảnh bìa:
   * 1. Chuyển đổi sang AVIF qua gateway
   * 2. Lấy presigned URL upload lên math-bucket (R2)
   * 3. Upload file AVIF trực tiếp lên R2
   */
  async uploadCoverImage(
    entityId: number,
    file: Blob,
    onStage?: (stage: ImageUploadStage) => void,
    folder: 'courses/course' | 'courses/lesson' = 'courses/course',
    currentUrl?: string | null,
  ): Promise<string> {
    onStage?.('converting')
    const avifBlob = await convertImageToAvif(file)

    onStage?.('presigning')
    const presignRes = (
      await client.request<{ upload_url: string; url: string; content_type: string }>({
        url: '/v1/admin/storage/presign',
        method: 'POST',
        data: {
          content_type: 'image/avif',
          folder,
          entity_id: entityId,
          asset_name: 'image',
          extension: 'avif',
        },
      })
    ).data

    onStage?.('uploading')
    let uploadResponse: Response
    try {
      uploadResponse = await fetch(presignRes.upload_url, {
        method: 'PUT',
        headers: {
          'Content-Type': presignRes.content_type,
        },
        body: avifBlob,
      })
    } catch {
      throw new Error('Không thể tải ảnh lên kho lưu trữ R2. Vui lòng kiểm tra lại mạng.')
    }

    if (!uploadResponse.ok) {
      throw new Error(`Upload ảnh lên R2 thất bại (${uploadResponse.status})`)
    }

    return nextAssetVersionUrl(presignRes.url, currentUrl)
  },

  // Lessons (Lazy loaded per course)
  async listLessons(courseId: number): Promise<Lesson[]> {
    return (await client.request<Lesson[]>({ url: `/v1/admin/courses/${courseId}/lessons`, method: 'GET' })).data
  },

  async getLesson(id: number): Promise<Lesson> {
    return (await client.request<Lesson>({ url: `/v1/admin/lessons/${id}`, method: 'GET' })).data
  },

  async createLesson(courseId: number, payload: LessonDraft): Promise<Lesson> {
    return (await client.request<Lesson>({
      url: `/v1/admin/courses/${courseId}/lessons`,
      method: 'POST',
      data: payload,
    })).data
  },

  async updateLesson(id: number, payload: Partial<LessonDraft>): Promise<Lesson> {
    return (await client.request<Lesson>({
      url: `/v1/admin/lessons/${id}`,
      method: 'PUT',
      data: payload,
    })).data
  },

  async deleteLesson(id: number): Promise<void> {
    await client.request<void>({ url: `/v1/admin/lessons/${id}`, method: 'DELETE' })
  },
}
